import cx from 'classnames'
import { Navigate, useParams } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { EmissionReportStatus } from '@/api/EmissionReportModel'
import {
  useEmissionsReportCalculatedEmissions,
  useSubmitEmissionReport,
  useMethodEmissionReports,
  useEmissionReport
} from '@/hooks/api/emissionReports'
import {
  Badge,
  BreadcrumbsBar,
  H2,
  IconButton,
  PageSection,
  parsePeriodId
} from '@/common'
import { LoadingScreen } from '@/shared/components'
import CarbonTaxPaidSection from './carbonTaxPaid/CarbonTaxPaidSection'
import InputsSection from './inputs/InputsSection'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import MethodReport, { isMethodEmissionReportComplete } from './MethodReport'
import IntegratedElectricityReportSection from './electricity/IntegratedElectricityReportSection'
import { useInstallations } from '@/features/installations/hooks/useInstallations'
import gridEmissionFactorByCountry from '@/shared/config/gridEmissionFactorByCountry.json'
import { EmissionTypeToReport } from './process/ProcessEmissionsSection'
import { AssetWithMetadata } from './process/ProcessEmissionsSection'
import { uniq } from 'es-toolkit'
import useI18n from '@/shared/hooks/useI18n'
import EditActivityLevelDialog from './dialogs/EditActivityLevelDialog'
import { useDialog } from '@/shared/hooks/useDialog'
import { CaretDownIcon } from '@/common/components/icons'
import ReportHeaderSection from './components/ReportHeaderSection'
import { ElectricityProductionMethod } from '@/common'

export default function ReportView() {
  const { t, dateLongFormatterMonthYear } = useI18n()
  const reportId = useParams<{ reportId: string }>().reportId!
  const installationId = useParams<{ installationId: string }>().installationId!
  const periodId = useParams<{ periodId: string }>().periodId!

  const { startDate: productionRouteStartDate, endDate: productionRouteEndDate } =
    parsePeriodId(periodId)

  const installationsQuery = useInstallations()
  const installation = installationsQuery.data?.find(
    installation => installation.id === installationId
  )
  const reportingPeriodBasis = installation?.basisForReportingPeriod

  const { productionMethodsByCode, assetsById } = useAppConfig().config
  const [openMethod, setOpenMethod] = useState<Record<string, boolean>>({})
  const { data: report, isLoading: isLoadingEmissionReport } = useEmissionReport(reportId)
  const methodEmissionsReportsQuery = useMethodEmissionReports(reportId)
  const methodEmissionReports = useMemo(
    () => methodEmissionsReportsQuery.data || [],
    [methodEmissionsReportsQuery.data]
  )
  const isLoadingMethodEmissionReports = methodEmissionsReportsQuery.isPending
  const activityLevel = report?.methodEmissionReports[0]?.activityLevel

  const {
    open: isEditActivityLevelDialogOpen,
    openDialog: openEditActivityLevelDialog,
    closeDialog: closeEditActivityLevelDialog
  } = useDialog()

  const { mutateAsync: submitEmissionReport, isPending: isSubmitting } =
    useSubmitEmissionReport(reportId)
  const { data: calculatedEmissionsForAllMethods, refetch: refetchCalculatedEmissions } =
    useEmissionsReportCalculatedEmissions(reportId)
  const methodsOrder = useMemo(
    () => report?.productionRoute?.productionMethods.map(method => method.code) || [],
    [report]
  )

  const submit = useCallback(async () => {
    await submitEmissionReport()
    toast.success(t('report:notifications.submitReportSuccess'))
  }, [submitEmissionReport, t])

  // If there is only one method, open it by default
  useEffect(() => {
    if (methodsOrder.length === 1 && Object.keys(openMethod).length === 0) {
      const openMethods = methodsOrder.reduce<Record<string, boolean>>(
        (acc, method) => {
          acc[method] = true
          return acc
        },
        {
          INTEGRATED: true,
          INTEGRATED_STATUS: true
        }
      )
      setOpenMethod(prev => ({ ...prev, ...openMethods }))
    }
  }, [methodsOrder, openMethod])

  useEffect(() => {
    refetchCalculatedEmissions()
  }, [report, methodEmissionReports, refetchCalculatedEmissions])

  const sortedMethodEmissionReports = methodEmissionReports?.sort(
    (methodEmissionReportA, methodEmissionReportB) =>
      methodsOrder.indexOf(methodEmissionReportA.productionMethodCode) -
      methodsOrder.indexOf(methodEmissionReportB.productionMethodCode)
  )

  const lastMethodReportIndex = (sortedMethodEmissionReports.length || 0) - 1
  const methodEmissionReport = sortedMethodEmissionReports[lastMethodReportIndex]
  const productionMethodCode = methodEmissionReport?.productionMethodCode
  const productionMethodDefinition = productionMethodsByCode[productionMethodCode]

  const isApplicableForActivityLevelValidation = Boolean(
    productionMethodDefinition?.isApplicableForActivityLevelValidation
  )

  const usesMassBalance = Boolean(productionMethodDefinition?.usesMassBalance)

  useEffect(() => {
    if (activityLevel !== undefined && !usesMassBalance) {
      if (activityLevel === null) {
        openEditActivityLevelDialog()
      }
    }
  }, [activityLevel, openEditActivityLevelDialog, usesMassBalance])

  if (isLoadingEmissionReport || isLoadingMethodEmissionReports) {
    return <LoadingScreen />
  }

  if (!report) {
    return <Navigate to="/" />
  }

  const { productionRoute, startDate, endDate, electricityProductionConfig } = report

  const formattedProductionRoutePeriod =
    reportingPeriodBasis === 'CALENDAR_YEAR'
      ? `${new Date(productionRouteStartDate).getFullYear()}`
      : `${dateLongFormatterMonthYear(new Date(productionRouteStartDate))} - ${dateLongFormatterMonthYear(
          new Date(productionRouteEndDate)
        )}`

  const isSameMonthAndYear =
    new Date(startDate).getFullYear() === new Date(endDate).getFullYear() &&
    new Date(startDate).getMonth() === new Date(endDate).getMonth()

  const formattedReportingPeriod = isSameMonthAndYear
    ? dateLongFormatterMonthYear(new Date(startDate))
    : `${dateLongFormatterMonthYear(new Date(startDate))} - ${dateLongFormatterMonthYear(
        new Date(endDate)
      )}`

  const productionRouteName = productionRoute.name

  const assetsToReport: AssetWithMetadata[] = productionRoute.assets.map(productionRouteAsset => {
    const { assetDefinitionId } = productionRouteAsset
    const assetDefinition = assetDefinitionId ? assetsById[assetDefinitionId] : null
    const emissionRecords = report?.methodEmissionReports.flatMap(methodEmissionReport => {
      return methodEmissionReport.emissionsRaw
        ? methodEmissionReport.emissionsRaw.filter(er => er.asset.id === productionRouteAsset.id)
        : []
    })
    return {
      productionRouteAsset,
      assetDefinition,
      emissionRecords
    }
  })

  const emissionsToReport: EmissionTypeToReport[] = assetsToReport.flatMap(asset => {
    const { assetDefinition, productionRouteAsset, emissionRecords } = asset

    const emissionTypes = productionRouteAsset.usesFuel
      ? uniq([...(assetDefinition?.emissionTypes || []), 'COMBUSTION'])
      : [...(assetDefinition?.emissionTypes || [])]

    const methods = productionRouteAsset.usesFuel
      ? uniq([...(assetDefinition?.calculationMethods || []), 'COMBUSTION'])
      : [...(assetDefinition?.calculationMethods || [])]

    return emissionTypes.map(emissionType => {
      const methodsForEmissionType = methods.filter(method => {
        const emisionTypesFromMethod = method.split('.')[0]
        return emisionTypesFromMethod === emissionType
      })

      const emissionRecord = emissionRecords.find(er => er.emissionType === emissionType) || null
      return {
        emissionType,
        possibleMethods: methodsForEmissionType,
        productionRouteAsset,
        assetDefinition,
        emissionRecord
      }
    })
  })

  const isIntegratedProcess = sortedMethodEmissionReports.length > 1
  const isPrecursorRequired = productionMethodDefinition?.isPrecursorRequired

  const precursorAggGoodCategory = productionMethodDefinition?.precursorAggGoodCategory || []

  const qualifyingSourceTypes: ElectricityProductionMethod[] = [
    ElectricityProductionMethod.GRID,
    ElectricityProductionMethod.DIRECT_TECHNICAL_LINK,
    ElectricityProductionMethod.PPA
  ]

  const electricitySources = productionRoute.electricityProductionMethods
  const qualifyingSourcesInRoute = electricitySources.filter(method =>
    qualifyingSourceTypes.includes(method.type)
  )
  const hasMultipleQualifyingSources = qualifyingSourcesInRoute.length > 1
  const areAllQualifyingSourcesZero = hasMultipleQualifyingSources
    ? qualifyingSourcesInRoute.every(
        method => (electricityProductionConfig?.[method.type]?.consumption ?? 0) === 0
      )
    : false

  const isElectricityConfigComplete =
    electricityProductionConfig &&
    Object.values(electricityProductionConfig).every(method => {
      return (
        method.consumption >= 0 &&
        method.emissionFactor >= 0 &&
        areAllQualifyingSourcesZero !== true
      )
    })

  const hasAnyPrecursor = report.precursorMaterials.length > 0

  const {
    arePrecursorsComplete,
    arePrecursorsWithinReportScope,
    isElectricityComplete: isProductionMethodElectricityComplete,
    completion: methodCompletion
  } = isMethodEmissionReportComplete({
    methodEmissionReport,
    productionRoute,
    emissionsToReport,
    precursors: report.precursorMaterials || [],
    precursorAggGoodCategory,
    usesMassBalance,
    isApplicableForActivityLevelValidation,
    isPrecursorRequired
  })

  const isElectricityComplete =
    isProductionMethodElectricityComplete &&
    isElectricityConfigComplete &&
    !areAllQualifyingSourcesZero

  const integratedCompletion = [
    {
      id: 'carbonTaxPaid',
      isWithinReportScope: true,
      complete: Boolean(report.carbonTaxPaidRaw),
      isOptional: false
    },
    {
      id: 'electricity',
      isWithinReportScope: true,
      complete: isElectricityConfigComplete,
      isOptional: false
    },
    ...methodCompletion
  ]

  const sectionsWithinReportScope = integratedCompletion.filter(item => item.isWithinReportScope)
  const completeSections = sectionsWithinReportScope.filter(item => item.complete)
  const percentageComplete = (completeSections.length / sectionsWithinReportScope.length) * 100

  const isViewMode = report.status === EmissionReportStatus.SUBMITTED

  const calculationForLastProductionMethod =
    calculatedEmissionsForAllMethods?.[lastMethodReportIndex]

  const allMethodsPercentages = sortedMethodEmissionReports.map(
    method =>
      isMethodEmissionReportComplete({
        methodEmissionReport: method,
        productionRoute,
        precursors: report.precursorMaterials || [],
        precursorAggGoodCategory,
        isApplicableForActivityLevelValidation,
        isPrecursorRequired: Boolean(
          productionMethodsByCode[method.productionMethodCode]?.isPrecursorRequired
        ),
        usesMassBalance: Boolean(
          productionMethodsByCode[method.productionMethodCode]?.usesMassBalance
        )
      }).percentageComplete
  )

  const installationCountry = installation?.country || ''
  const gridEmissionFactor =
    gridEmissionFactorByCountry[installationCountry as keyof typeof gridEmissionFactorByCountry] ||
    0

  const allElectricityMethods = productionRoute.electricityProductionMethods

  return (
    <div className="relative">
      <BreadcrumbsBar
        links={[
          { label: t('common:breadcrumbs.dashboard'), to: '/dashboard' },
          {
            label: `${productionRouteName} - ${formattedProductionRoutePeriod}`,
            to: `/dashboard/${installationId}/production-routes/${productionRoute.id}/reports/${periodId}`
          },
          { label: formattedReportingPeriod, to: '' }
        ]}
      />
      <div className="px-6 py-5">
        <header className="space-y-1">
          <div className="flex items-center gap-2">
            <H2>{productionRouteName}</H2>
            {report.status === EmissionReportStatus.DRAFT && (
              <Badge variant="blue">{t('report:status:DRAFT')}</Badge>
            )}
            {report.status === EmissionReportStatus.SUBMITTED && (
              <Badge variant="green">{t('report:status:SUBMITTED')}</Badge>
            )}
          </div>
          <div>
            <p className="text-text-secondary text-sm">
              {formattedProductionRoutePeriod} | {formattedReportingPeriod}
            </p>
          </div>
        </header>
      </div>
      <ReportHeaderSection
        isIntegratedProcess={isIntegratedProcess}
        startDate={startDate}
        endDate={endDate}
        allMethodsPercentages={allMethodsPercentages}
        productionRoute={productionRoute}
        submit={submit}
        isSubmitting={isSubmitting}
        calculatedEmissionsForAllMethods={calculatedEmissionsForAllMethods || []}
        openEditActivityLevelDialog={openEditActivityLevelDialog}
        percentageComplete={percentageComplete}
        report={report}
        isActivityLevelRequired={!usesMassBalance}
        activityLevel={calculationForLastProductionMethod?.activityLevel || 0}
      />
      <div className="px-6 pb-5">
        <main className="mt-6 space-y-4">
          <section className="space-y-4">
            <IntegratedElectricityReportSection
              electricityProductionMethods={allElectricityMethods}
              isComplete={isElectricityComplete}
              gridEmissionFactor={gridEmissionFactor}
              reportId={reportId}
              isViewMode={isViewMode}
              calculatedEmissionsForAllMethods={calculatedEmissionsForAllMethods || []}
              configByElectricityMethod={electricityProductionConfig}
              methodEmissionReports={sortedMethodEmissionReports}
            />
            {arePrecursorsWithinReportScope && (
              <InputsSection
                installationId={report.installationId}
                emissionReportId={reportId}
                methodEmissionReportId={methodEmissionReport?.id}
                isViewMode={isViewMode}
                isApplicableForActivityLevelValidation={isApplicableForActivityLevelValidation}
                isComplete={arePrecursorsComplete}
                emissions={calculationForLastProductionMethod?.totalPrecursorDirectEmissions || 0}
                reportStartDate={startDate}
                reportEndDate={endDate}
              />
            )}
          </section>
          {sortedMethodEmissionReports.map((methodEmissionReport, index) => {
            const { productionMethodCode, id } = methodEmissionReport
            const calculatedEmission = calculatedEmissionsForAllMethods?.find(
              emission => emission.id === methodEmissionReport.id
            )
            const nextMethodEmissionReportId = sortedMethodEmissionReports?.[index + 1]?.id

            return (
              <MethodReport
                key={id}
                hasAnyPrecursor={hasAnyPrecursor}
                reportId={reportId}
                isIntegratedProcess={isIntegratedProcess}
                nextMethodEmissionReportId={nextMethodEmissionReportId}
                productionRoute={productionRoute}
                methodEmissionReport={methodEmissionReport}
                calculatedEmission={calculatedEmission}
                index={index}
                isOpen={openMethod[productionMethodCode]}
                toggle={() =>
                  setOpenMethod(prev => ({
                    ...prev,
                    [productionMethodCode]: !prev[productionMethodCode]
                  }))
                }
                isViewMode={isViewMode}
              />
            )
          })}

          {isIntegratedProcess ? (
            <PageSection
              title={t('report:sections.productionRouteLevel')}
              actions={
                <IconButton
                  onClick={() =>
                    setOpenMethod(prev => ({ ...prev, ['INTEGRATED']: !prev['INTEGRATED'] }))
                  }
                  variant="txt-default"
                  icon={
                    <CaretDownIcon
                      className={`transition-transform duration-150 ease-linear origin-center ${
                        openMethod['INTEGRATED'] ? 'rotate-180' : ''
                      }`}
                    />
                  }
                />
              }
            >
              <div
                className={cx('transition-all duration-300 overflow-hidden ease-in', {
                  'h-calc-auto opacity-100': openMethod['INTEGRATED'],
                  'h-0 opacity-0': !openMethod['INTEGRATED']
                })}
              >
                <CarbonTaxPaidSection
                  reportId={reportId}
                  isViewMode={isViewMode}
                  isComplete={!!report.carbonTaxPaidRaw}
                  carbonTaxPaid={report.carbonTaxPaidRaw || null}
                />
              </div>
            </PageSection>
          ) : (
            <div className="mt-4">
              <CarbonTaxPaidSection
                reportId={reportId}
                isViewMode={isViewMode}
                isComplete={!!report.carbonTaxPaidRaw}
                carbonTaxPaid={report.carbonTaxPaidRaw || null}
              />
            </div>
          )}
        </main>
      </div>
      <EditActivityLevelDialog
        isOpen={isEditActivityLevelDialogOpen}
        activityLevel={activityLevel}
        close={closeEditActivityLevelDialog}
        reportId={reportId}
        methodEmissionReportId={methodEmissionReport?.id}
      />
    </div>
  )
}
